import { SupportedCountry } from "@brexhq/component-library";

import {
  LeadSelfAttestedType,
  QuestionEmployeeCount,
  QuestionFundingSource,
  QuestionFundingSourceV2,
  SalesVolumeV2,
  LeadAttributionMetadata,
  EmployeeCountV3,
  CompanyTypeV3,
  FundingSourceV3,
  SalesVolumeV3,
  SalesSegment,
} from "@brexhq/next/types/brex-api";
import { SpacingValues } from "@brexhq/shared/types/SpacingScale";

import { useLeadFormFields } from "./hooks/useLeadFormFields";
import { leadFormMachineContext } from "./machines/types";

export const CONTROL = "CONTROL";
export const TREATMENT = "TREATMENT";
export const HOW_DID_YOU_HEAR_ABOUT_US_MAX_LENGTH = 100;
export const PLEASE_USE_WORK_EMAIL_MESSAGE = "Please use your work email.";
export const PLEASE_USE_A_VALID_WEBSITE_MESSAGE =
  "Please use a valid website URL.";
export const INVALID_INPUT_MESSAGE = "Invalid input.";
export const BACK_BUTTON_TEST_ID = "BACK_BUTTON";
export const SUBMIT_BUTTON_TEST_ID = "SUBMIT_BUTTON";

export const ERROR_BOX_MESSAGE =
  "Oops! Something went wrong here — please try again. If the error continues please reach out to support.";
export const EMPOWER_MULTI_STEP_LEAD_FORM = "Empower multi-step lead form";

export const LEAD_FORM_PROGRAM_NAME =
  "CP Sales Lead Form - Contact Sales (MMDG;cp_leadform)";

export const LEAD_FORM_PROGRAM_NAME_NO_CHILIPIPER =
  "MM Sales Lead Form - Contact Sales (MMDG;leadform)";

/**
 * Types to determine what to features to use
 * for the LeadFormController
 */
export enum FormType {
  /**
   * Used for any Sanity instance (schema automatically applies this value)
   */
  GTM_LEAD_FORM = "GTM_LEAD_FORM",

  /**
   * Anything coded in, like /sales and /signup currently use this type.
   */
  SALES_LEAD_FORM = "SALES_LEAD_FORM",

  /**
   * Event based lead form, sales will use this form to onboard customers from the offline
   * events.
   */
  EVENTS_LEAD_FORM = "EVENTS_LEAD_FORM",
}

/**
 * Enum for form
 */
export enum FormFields {
  workEmail = "workEmail",
  firstName = "firstName",
  lastName = "lastName",
  mobileNumber = "mobileNumber",
  companyName = "companyName",
  employeeCount = "employeeCount",
  isEcomm = "isEcomm",
  fundingSource = "fundingSource",
  monthlySales = "monthlySales",
  /**
   * TODO: [M7] Erase comment once experiment is done
   */
  website = "website",
  hasNoWebsite = "hasNoWebsite",
  password = "password",
  industry = "industry",
  linkedinProfileUrl = "linkedinProfileUrl",
  fundingSources = "fundingSources",
}
/**
 * Values we're sending to update lead
 */
export type LeadFormValues = {
  firstName: string;
  lastName: string;
  workEmail: string;
  phone?: number;
  companyName: string;
  country?: SupportedCountry;
  employeeCount?: QuestionEmployeeCount | EmployeeCountV3;
  refCode?: string;
  isEcomm?: LeadSelfAttestedType | CompanyTypeV3;
  fundingSource?: QuestionFundingSource;
  monthlySales?: SalesVolumeV2 | SalesVolumeV3;
  mobileNumber?: string;
  website?: string;
  linkedinProfileUrl?: string;
  fundingSources?: QuestionFundingSourceV2[] | FundingSourceV3[];
  industryLabel?: string;
  hasNoWebsite?: boolean;
  industry?: string;
  password: string;
  salesRepName?: string;
  salesSegment?: SalesSegment;
};

export type Fields = ReturnType<typeof useLeadFormFields>["fields"];

export type StepProps = {
  stateMachineCtx?: leadFormMachineContext;
};

export type HiddenFields = Pick<
  LeadAttributionMetadata,
  | "contentType"
  | "contentTag"
  | "webinarId"
  | "videoId"
  | "eventId"
  | "pageSegment"
  | "brexSolution"
  | "brexFeature"
  | "salesforceCampaign"
>;

export interface FormMetaProps {
  /**
   * Program name for the Marketo campaign, we push this value along to the create/update endpoints
   */
  customProgramName: string;

  /**
   * Used with sanity. Sets the form titles for each step
   */
  customFormTitle?: string;

  /**
   * Used with Sanity. Sets the submit button labels for the form
   */
  customSubmitButtonLabel?: string;

  /**
   * Used with Sanity. Sets the form title for an existing lead
   */
  customExistingLeadFormTitle?: string;
  /**
   * Used with Sanity. Sets the submit button for an existing lead
   */
  customExistingLeadSubmitButtonLabel?: string;

  /**
   * Used with Sanity. Checks if the user is using a work email
   */
  checkWorkEmail?: boolean;

  /**
   * Used with Sanity. Uses the inline email input instead
   */
  isInlineEmail?: boolean;

  /**
   * Used with Sanity. Sets the width of the inline email input
   */
  inlineEmailFieldWidth?: string;
  /**
   * Used with Sanity. Sets a top margin on the component
   */
  marginTop?: SpacingValues;

  /**
   * If success content if provided this is not needed, but if not,
   * we use this instead of showing them a success content. Upon completing
   * the form steps, we will redirect them to the designated url.
   */
  bypassSuccessContentUrl?: {
    to: string | null;
    toExternal: string | null;
    noFollow?: boolean | null;
  };

  /**
   * Used with Sanity.
   * Extra meta data we want to associate the marketo form with. Not shown on the UI
   */
  hiddenFields?: HiddenFields;

  /**
   * Type is to identify what kind of machine to use the component with
   */
  formType: string | FormType;
}
